/**
 This hooks used to sort the Performance Analysis stacked chart order based on the Sort By dropdown.
 The type of orders are Alphabetically Ascending, Alphabetically Descending, Absolute Ascending and Absolute Descending.
 The order will only use retrieved response from stacked chart API, and no API call back when the sort by dropdown changed.
 */
import {SeriesResp, StackedChartResp} from '@data-visualization/stacked-chart/Types';
import {SortDirectionType, SortFieldDisplayProps} from '@components/design/migrated/Table';
import {SortDirection} from '@components/design/migrated/Table/Sorting/types';
import {isEmpty} from 'lodash';
import {SortKeys} from '@models/PerformanceAnalysisboard';
import {getGrandTotalValue} from '@utils/TotalUtils';

const RANGED_VALUE_LIST: string[] = ['<', '<=', '>=', '>'];
const RANGED_VALUE: number = 0;
const NUMBER_VALUE_INDEX: number = 1;

const useStackedChartSorting = () => {
  /**
   * Sort Stacked Chart vertically which will sort the segment column on Stacked Chart view
   * @param stackedChartResps is the response from Stacked Chart API
   * @param sortFieldDisplay is an object stores the Sort By options
   * Slice the list contains the TOTAl segment, since TOTAL segment should always be the first column
   * Slice the remaining segments into a list, and sort them either alphabetically or absolute kpi value
   * The alphabetical sorting is based on each segment label
   * The absolute KPI value is based on segmented.share.data[1] which is the total absolute value of the each segment based on Focus Period
   * Return the list based on ascending or descending order, the TOTAL segment will be concatenated back as first in the list.
   */
  const sortStackedChartVertically = (
    stackedChartResps: StackedChartResp[],
    sortFieldDisplay: SortFieldDisplayProps,
  ): StackedChartResp[] => {
    const itemTotal = stackedChartResps.filter((item) => item.segment === getGrandTotalValue());

    const itemsWithoutTotal = stackedChartResps.filter((item) => item.segment !== getGrandTotalValue());

    if (sortFieldDisplay.sortKey === SortKeys.Alphabetically) {
      const sortedItemsAlphabeticallyWithDirection =
        sortFieldDisplay.sortDirection === SortDirection.asc
          ? itemsWithoutTotal.slice().sort((a, b) => stackedChartCustomComparator(a.segment, b.segment))
          : itemsWithoutTotal.slice().sort((a, b) => stackedChartCustomComparator(b.segment, a.segment));

      return itemTotal.concat(...sortedItemsAlphabeticallyWithDirection);
    }

    const sortedAbsoluteValueWithDirection =
      sortFieldDisplay.sortDirection === SortDirection.asc
        ? itemsWithoutTotal.slice().sort((a, b) => a.data[1].value - b.data[1].value)
        : itemsWithoutTotal.slice().sort((a, b) => b.data[1].value - a.data[1].value);

    return itemTotal.concat(...sortedAbsoluteValueWithDirection);
  };

  /**
   * Sort Stacked Chart Horizontally, each rows is sorted based on Alphabetical or Absolute KPI value
   * @param stackChartData is the Stacked Chart response data from API
   * @param sortFieldDisplay is an object stores the Sort By options
   * Sort the horizontal data on the chart(each rows) based on share and data array
   * For alphabetical order, each of stackChartData.share.series.label sorted based Alphabetical ascending or descending
   * Theres no need to order stackChartData.share.data for alphabetical since its only contain absolute value of the series
   * For Absolute KPI order, each of stackChartData.share.series.data[1].value which is the focus period is sorted based on ascending or descending
   * While stackChartData.share.data[1].value which is the total absolute value for each row is also sorted based on ascending or descending
   * Return sorted array alphabetically ascending or descending, or absolute KPI value ascending or descending
   */

  const sortStackedChartHorizontally = (
    stackChartData: StackedChartResp[],
    sortFieldDisplay: SortFieldDisplayProps,
  ) => {
    if (sortFieldDisplay.sortKey === SortKeys.Alphabetically) {
      return stackChartData.map((stacked) => ({
        ...stacked,
        share: {
          ...stacked.share,
          series: sortSeriesAlphabetically(stacked.share.series, sortFieldDisplay.sortDirection),
          data: stacked.share.data,
        },
      }));
    }

    return stackChartData.map((stacked) => ({
      ...stacked,
      share: {
        ...stacked.share,
        series: sortSeriesAbsoluteValue(stacked.share.series, sortFieldDisplay.sortDirection),
        data: stacked.share.data,
      },
    }));
  };
  const sortStackedChartRespData = (
    data: StackedChartResp[],
    stackedChartSortingOrder: SortFieldDisplayProps,
  ): StackedChartResp[] => {
    const sortedStackedChartDataVertically = sortStackedChartVertically(data, stackedChartSortingOrder);

    return sortStackedChartHorizontally(sortedStackedChartDataVertically, stackedChartSortingOrder);
  };
  return {
    sortStackedChartVertically,
    sortStackedChartHorizontally,
    sortStackedChartRespData,
  };
};

export default useStackedChartSorting;

/**
 * To sort the series(rows data on stacked chart) alphabetically ascending or descending
 * @param series is the series array from stacked chart response data
 * @param sortDirection sort direction either ascending or descending
 * Slice the series and sort it using custom comparator
 * Slice series with label "Others" to concat as the last order
 * Return the order of left side of the stacked chart legends
 * For example, alphabetically ascending will be APPLE, SAMSUNG, SONOS and OTHER
 */
const sortSeriesAlphabetically = (series: SeriesResp[], sortDirection: SortDirectionType) => {
  const seriesOthers = series.filter((itemOthers) => itemOthers.label.toLowerCase() === 'others');
  const seriesWithoutOthers = series.filter((eachSeries) => eachSeries.label.toLowerCase() !== 'others');
  const sortedSeriesAlphabetically =
    sortDirection === SortDirection.asc
      ? seriesWithoutOthers.slice().sort((a, b) => stackedChartCustomComparator(a.label, b.label))
      : seriesWithoutOthers.slice().sort((a, b) => stackedChartCustomComparator(b.label, a.label));

  return sortedSeriesAlphabetically.concat(...seriesOthers);
};

/**
 * To sort the series(segmented data on stacked chart) based on absolute KPI value
 * @param series is the series array from stacked chart response data
 * @param sortDirection sort direction either ascending or descending
 * Slice the series array and sort it based on the absolute KPI value
 * Slice series with label "Others" to concat as the last order
 * For example, segment TOTAL series[0].data[1].value (series[0] is brand SONOS and data[1] is focus period) contribute 30,
 * and segment TOTAL series[1].data[1].value (series[1] is brand APPLE and data[1] is focus period) contribute 65,
 * then this position will be swapped.
 * Then it will return the array based on ascending or descending
 */
const sortSeriesAbsoluteValue = (series: SeriesResp[], sortDirection: SortDirectionType) => {
  const seriesOthers = series.filter((itemOthers) => itemOthers.label.toLowerCase() === 'others');
  const seriesWithoutOthers = series.filter((eachSeries) => eachSeries.label.toLowerCase() !== 'others');
  const sortedSeriesAbsolute =
    sortDirection === SortDirection.asc
      ? seriesWithoutOthers.slice().sort((a, b) => a.data[1].value - b.data[1].value)
      : seriesWithoutOthers.slice().sort((a, b) => b.data[1].value - a.data[1].value);

  return sortedSeriesAbsolute.concat(...seriesOthers);
};

/**
 * To sort the segment alphabetically ascending or descending
 * @param paramA is the segment(label) for first argument
 * @param paramB is the segment(label for second argument
 * The segment will have various of value, e.g. Priceband, Channel, DEVICE TYPE
 * Returns value -1, 0 or 1 for comparison of two argument based on string comparison, range or number comparison
 */
const stackedChartCustomComparator = (paramA: string, paramB: string): number => {
  const SPLIT_STRING_BY_SPACE_AND_SYMBOL = /([^\w\s]+|\d+|\S+)/g;

  const matchRegexA = paramA.split(SPLIT_STRING_BY_SPACE_AND_SYMBOL).filter((token) => token.trim() !== '');
  const matchRegexB = paramB.split(SPLIT_STRING_BY_SPACE_AND_SYMBOL).filter((token) => token.trim() !== '');

  if (!matchRegexA || !matchRegexB) {
    return paramA.localeCompare(paramB);
  }

  if (isRangedValues(paramA) && isRangedValues(paramB)) {
    const { formattedRangeParamA, formattedRangeParamB } = formatRangeValues(matchRegexA, matchRegexB);
    if (
      formattedRangeParamA.length > 1 &&
      formattedRangeParamB.length > 1 &&
      isNumeric(formattedRangeParamA[NUMBER_VALUE_INDEX]) &&
      isNumeric(formattedRangeParamB[NUMBER_VALUE_INDEX])
    ) {
      return sortRangeValues(formattedRangeParamA, formattedRangeParamB);
    }
  }

  // For situation where ranged value comparing with non-ranged value
  // e.g ">4K" comparing with "4k"
  // ">4k" should be first
  if (isRangeValueWithNonRange(paramA, paramB)) {
    return -1;
  }
  // For situation where non-ranged value comparing with ranged value
  // e.g "4k" comparing with ">4K"
  // ">4k" should be first
  if (isRangeValueWithNonRange(paramB, paramA)) {
    return 1;
  }
  const sortedMixFeatureValue = sortMixedFeatureValues(matchRegexA, matchRegexB);

  if (sortedMixFeatureValue !== -2) {
    return sortedMixFeatureValue;
  }

  return paramA.localeCompare(paramB);
};

/**
 * To check if the parameter is type of range value
 * @param param
 * Returns true or false if the string param contains range symbol
 * For example, <= 90  will return true
 */
export const isRangedValues = (param: string) => {
  const operators: string[] = ['<', '<=', '>=', '>'];

  return operators.some((value: string) => param.includes(value));
};

/**
 * To format the two array string to have correct array index for numeric
 * @param paramA string array 1
 * @param paramB string array 2
 * Validate if index 0 (first array) of the array is not numeric value, therefore add empty array at position 1(index 0)
 * For example ["90", ">"] will be ["", "90", ">"]
 * This is to make sure to standardize the comparison of range value on index 1
 */
export const formatRangeValues = (paramA: string[], paramB: string[]) => {
  if (!isRangedValues(paramA[0])) {
    paramA.unshift('');
  }
  if (!isRangedValues(paramB[0])) {
    paramB.unshift('');
  }
  return { formattedRangeParamA: paramA, formattedRangeParamB: paramB };
};

/** To validate if the string is numeric value
 *
 * @param numStr
 * Returns true or false if string contains numeric value
 * For example, <OTHER> will return false
 */
export const isNumeric = (numStr: string) => {
  const regexPattern = /-?\d+(\.\d+)?/;
  if (isEmpty(numStr)) {
    return false;
  }
  return RegExp(regexPattern).exec(numStr);
};

/**
 * To sort the range values according to the correct order
 * @param rangeValueA
 * @param rangeValueB
 * The range values would come in this format
 * ["<", "100", "", ""]
 * [">", "100", "<", "200"]
 * [">", "200", "<", "300"]
 * [">", "300", "", ""]
 * The idea is to compare the whether the number values are identical,
 * then it would be comparing the ranged symbol based on predefined order
 * e.g ["<", "<=", ">=", ">"]
 * Else it would be comparing which the first number values
 *
 */
export const sortRangeValues = (rangeValueA: string[], rangeValueB: string[]) => {
  const rangeSymbolIndexA: number = RANGED_VALUE_LIST.indexOf(rangeValueA[RANGED_VALUE]);
  const rangeSymbolIndexB: number = RANGED_VALUE_LIST.indexOf(rangeValueB[RANGED_VALUE]);

  if (rangeValueA[NUMBER_VALUE_INDEX] === rangeValueB[NUMBER_VALUE_INDEX]) {
    if (rangeSymbolIndexA < 0) return 1;
    if (rangeSymbolIndexB < 0) return -1;

    if (rangeSymbolIndexA < rangeSymbolIndexB) {
      return -1;
    }
    if (rangeSymbolIndexA > rangeSymbolIndexB) {
      return 1;
    }
  }

  if (!isValidNumbericFormat(rangeValueA[NUMBER_VALUE_INDEX], rangeValueB[NUMBER_VALUE_INDEX])) {
    return 0;
  }

  //handle priceband with both thousand and decimal separators, e.g. >1,537.99
  if (hasDecimalAndThousandSeparators(rangeValueA, rangeValueB)) {
    let valA = appendThousand(rangeValueA);
    let valB = appendThousand(rangeValueB);

    return sortByDecimal(parseFloat(valA), parseFloat(valB));
  }

  return sortByDecimal(parseFloat(rangeValueA[NUMBER_VALUE_INDEX]), parseFloat(rangeValueB[NUMBER_VALUE_INDEX]));
};

const appendThousand = (rangeValue: string[]) => {
  let val = rangeValue[NUMBER_VALUE_INDEX];
  if (rangeValue.length >= NUMBER_VALUE_INDEX + 5 && isCommaPeriodOrQuote(rangeValue[NUMBER_VALUE_INDEX + 3])) {
    val += rangeValue[NUMBER_VALUE_INDEX + 2];
  }
  return val;
};

const verifyDecimalAndThousandSeparators = (rangeValue: string[]) => {
  if (
    rangeValue.length >= NUMBER_VALUE_INDEX + 5 &&
    isCommaPeriodOrQuote(rangeValue[NUMBER_VALUE_INDEX + 1]) &&
    isCommaPeriodOrQuote(rangeValue[NUMBER_VALUE_INDEX + 3]) &&
    rangeValue[NUMBER_VALUE_INDEX + 1] !== rangeValue[NUMBER_VALUE_INDEX + 3]
  ) {
    return true;
  }
  return false;
};

const hasDecimalAndThousandSeparators = (rangeValueA: string[], rangeValueB: string[]) => {
  if (
    (rangeValueA.length > NUMBER_VALUE_INDEX + 3 || rangeValueB.length > NUMBER_VALUE_INDEX + 3) &&
    isValidNumbericFormat(rangeValueA[NUMBER_VALUE_INDEX + 2], rangeValueB[NUMBER_VALUE_INDEX + 2])
  ) {
    if (verifyDecimalAndThousandSeparators(rangeValueA) || verifyDecimalAndThousandSeparators(rangeValueB)) {
      return true;
    }
  }

  return false;
};

const isValidNumbericFormat = (valueA: string, valueB: string) => {
  if (!valueA || !valueB) {
    return false;
  }

  if (isCommaPeriodOrQuote(valueA) || isCommaPeriodOrQuote(valueB)) {
    return false;
  }

  if (!isDigit(valueA) || !isDigit(valueB)) {
    return false;
  }

  return true;
};

/**
 * Check if string is comma, period or quote (, . "")
 *
 * @param str split feature value
 * @return true or false
 */
export const isCommaPeriodOrQuote = (str: string) => {
  return RegExp(/[[\],."]/).exec(str);
};

/**
 * Check feature value contains only number values
 *
 * @param str feature value
 * @return true or false
 */
export const isDigit = (str: string) => {
  return str.split('').every((char) => char >= '0' && char <= '9');
};

/**
 * Sorting based on Decimal in ascending
 *
 * @return whether to move the value 1 step higher in position or lower
 * @param paramA
 * @param paramB
 */

export const sortByDecimal = (paramA: number, paramB: number): number => {
  if (paramA < paramB) {
    return -1;
  } else if (paramA > paramB) {
    return 1;
  } else {
    return 0;
  }
};

export const isRangeValueWithNonRange = (feature1: string, feature2: string) => {
  return isRangedValues(feature1) && !isRangedValues(feature2);
};

export const isRangeValue = (rangeValueA: string, rangeValueB: string) => {
  return isRangedValues(rangeValueA) && isRangedValues(rangeValueB); // && rangeValueA === rangeValueB;
};

export const isNumericWithNonNumeric = (valueA: string, valueB: string) => {
  return isNumeric(valueA) && !isNumeric(valueB);
};

export const isFeaturesDigit = (valueA: string, valueB: string) => {
  return isDigit(valueA) && isDigit(valueB);
};
export const sortMixedFeatureValues = (rangeValueListA: string[], rangeValueListB: string[]) => {
  for (let i = 0; i < rangeValueListA.length; i++) {
    if (isEmpty(rangeValueListA[i])) {
      return 1;
    }
    if (isEmpty(rangeValueListB[i])) {
      return -1;
    }
    // For situation where ranged value comparing with non-ranged value
    // e.g "<OTHER>" comparing with "<4K"
    // "<4K" should be first
    if (isRangeValue(rangeValueListA[i], rangeValueListB[i])) {
      if (isNumericWithNonNumeric(rangeValueListA[NUMBER_VALUE_INDEX], rangeValueListB[NUMBER_VALUE_INDEX])) {
        return -1;
      } else {
        return 1;
      }
    }
    if (isFeaturesDigit(rangeValueListA[i], rangeValueListB[i])) {
      return sortByDecimal(parseFloat(rangeValueListA[i]), parseFloat(rangeValueListB[i]));
    }
    if (rangeValueListA[i] !== rangeValueListB[i]) {
      return rangeValueListA[i].localeCompare(rangeValueListB[i]);
    }
  }
  return -2;
};
